import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { BarChart3, Users, Clock, AlertTriangle, FileText, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const DirectionDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const chartData = [
    { name: 'Kolwezi', total: stats?.territory_stats?.Kolwezi || 0 },
    { name: 'Mutshatsha', total: stats?.territory_stats?.Mutshatsha || 0 },
    { name: 'Lubudi', total: stats?.territory_stats?.Lubudi || 0 },
    { name: 'Dilolo', total: stats?.territory_stats?.Dilolo || 0 },
    { name: 'Kapanga', total: stats?.territory_stats?.Kapanga || 0 },
  ];

  return (
    <div className="bg-slate-100 min-h-[calc(100vh-4rem)]">
      {/* Executive Header */}
      <div className="bg-blue-950 text-white pb-16 pt-8 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-2 text-amber-400 text-sm font-bold uppercase tracking-wider mb-2">
                <BarChart3 className="w-4 h-4" />
                Gouvernance Provinciale
              </div>
              <h1 className="text-3xl font-bold mb-1">Tableau de bord exécutif</h1>
              <p className="text-blue-200">Indicateurs de performance Lualaba Services</p>
            </div>
            <div className="bg-blue-900/50 border border-blue-800 rounded-lg p-3 text-sm">
              <div className="text-blue-300 mb-1">Vue actuelle</div>
              <div className="font-semibold flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400"></div> Province entière (Tous secteurs)
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 -mt-8">
        {loading ? (
          <div className="bg-white p-8 rounded-xl shadow-sm text-center">Chargement des données...</div>
        ) : (
          <>
            {/* KPI Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-blue-600" data-testid="kpi-demandes">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">Demandes reçues</p>
                    <h3 className="text-3xl font-bold text-slate-900">{stats?.total_demandes || 0}</h3>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    <FileText className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-xs font-medium text-blue-600 flex items-center">
                  Total historique
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-emerald-500" data-testid="kpi-resolutions">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">Résolutions confirmées</p>
                    <h3 className="text-3xl font-bold text-slate-900">{stats?.resolutions_confirmees || 0}</h3>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-xs font-medium text-slate-500 flex items-center">
                  Taux de résolution: {stats?.total_demandes ? Math.round((stats.resolutions_confirmees / stats.total_demandes) * 100) : 0}%
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-amber-500" data-testid="kpi-delai">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">Délai moyen de réponse</p>
                    <h3 className="text-3xl font-bold text-slate-900">{stats?.delai_reponse_moyen_h || 0}<span className="text-lg text-slate-500 font-medium ml-1">h</span></h3>
                  </div>
                  <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                    <Clock className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-xs font-medium text-amber-600 flex items-center">
                  Objectif: &lt; 48h
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-red-500" data-testid="kpi-retard">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">Dossiers en retard</p>
                    <h3 className="text-3xl font-bold text-slate-900">{stats?.dossiers_retard || 0}</h3>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-xs font-medium text-red-600 flex items-center">
                  Nécessite une attention immédiate
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Répartition par territoire (Déclaratif)</h3>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                      <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#1E3A8A' : '#3b82f6'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Dossiers Récents</h3>
                <div className="space-y-4">
                  {stats?.recent_alerts?.length > 0 ? (
                    stats.recent_alerts.map((alert, i) => (
                      <div key={i} className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex flex-col gap-2">
                        <div className="flex gap-3">
                          <FileText className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-blue-900 text-sm line-clamp-1">{alert.sujet}</h4>
                            <p className="text-xs text-blue-700 mt-1">Status: {alert.status}</p>
                          </div>
                        </div>
                        {alert.fichier_joint && (
                          <div className="ml-8">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${alert.fichier_joint}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 text-xs font-semibold underline">
                              Voir la pièce jointe
                            </a>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 italic">Aucune donnée récente</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DirectionDashboard;
